/**
 * AnalyticsHelpers - Typed analytics tracking methods
 *
 * These functions provide typed tracking methods for common events
 * across web and mobile platforms. Event names are sourced from
 * EventCatalog — components should use these helpers instead of
 * calling track() with raw event name strings.
 *
 * Accepts a PostHog client (posthog.capture) or any object with
 * a .capture() or .track() method.
 */

import { EVENTS } from './EventCatalog';

/**
 * Create a typed analytics tracker with helper methods
 * @param {object} client - PostHog client or any object with capture()/track()
 * @returns {object} Object with typed tracking methods
 */
export const createAnalyticsHelpers = (client) => {
  // Support both PostHog (.capture) and legacy (.track) interfaces
  const capture = (eventName, properties) => {
    if (typeof client.capture === 'function') {
      client.capture(eventName, properties);
    } else if (typeof client.track === 'function') {
      client.track(eventName, properties);
    }
  };

  return {
    // ==========================================
    // AUTHENTICATION EVENTS
    // ==========================================

    trackSignup: async (method, properties = {}) => {
      capture(EVENTS.SIGNUP_COMPLETED, {
        signup_method: method,
        ...properties
      });
    },

    trackLogin: async (method, properties = {}) => {
      capture(EVENTS.LOGIN_COMPLETED, {
        login_method: method,
        ...properties
      });
    },

    trackLogout: async (properties = {}) => {
      capture(EVENTS.LOGOUT_COMPLETED, properties);
    },

    trackPasswordReset: async (properties = {}) => {
      capture(EVENTS.PASSWORD_RESET_COMPLETED, properties);
    },

    // ==========================================
    // ONBOARDING EVENTS
    // ==========================================

    trackOnboardingStep: async (step, properties = {}) => {
      capture(EVENTS.ONBOARDING_STEP_VIEWED, {
        step,
        ...properties
      });
    },

    trackOnboardingComplete: async (properties = {}) => {
      capture(EVENTS.ONBOARDING_COMPLETED, properties);
    },

    trackExamSelection: async (exam, properties = {}) => {
      capture(EVENTS.EXAM_SELECTED, {
        exam_name: exam,
        ...properties
      });
    },

    trackAssessmentStart: async (properties = {}) => {
      capture(EVENTS.ASSESSMENT_STARTED, properties);
    },

    trackAssessmentComplete: async (score, properties = {}) => {
      capture(EVENTS.ASSESSMENT_COMPLETED, {
        score,
        ...properties
      });
    },

    trackOnboardingAssessmentSkipped: async (properties = {}) => {
      capture(EVENTS.ONBOARDING_ASSESSMENT_SKIPPED, properties);
    },

    trackOnboardingAssessmentQuizCompleted: async (properties = {}) => {
      capture(EVENTS.ONBOARDING_ASSESSMENT_QUIZ_COMPLETED, properties);
    },

    trackOnboardingAssessmentFeedback: async (properties = {}) => {
      capture(EVENTS.ONBOARDING_ASSESSMENT_FEEDBACK, properties);
    },

    // ==========================================
    // DIAGNOSTIC FLOW EVENTS
    // ==========================================

    trackDiagnosticStartClicked: async (exam, properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_START_CLICKED, {
        exam,
        ...properties
      });
    },

    trackDiagnosticSkipped: async (properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_SKIPPED, properties);
    },

    trackDiagnosticQuestionViewed: async (questionIndex, properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_QUESTION_VIEWED, {
        question_index: questionIndex,
        ...properties
      });
    },

    trackDiagnosticQuestionAnswered: async (questionIndex, properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_QUESTION_ANSWERED, {
        question_index: questionIndex,
        ...properties
      });
    },

    trackDiagnosticNavigation: async (direction, fromIndex, toIndex, properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_NAVIGATION, {
        direction,
        from_index: fromIndex,
        to_index: toIndex,
        ...properties
      });
    },

    trackDiagnosticEndTestTapped: async (properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_END_TEST_TAPPED, properties);
    },

    trackDiagnosticResultsViewed: async (properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_RESULTS_VIEWED, properties);
    },

    trackDiagnosticReviewFilterTapped: async (filter, properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_REVIEW_FILTER_TAPPED, {
        filter,
        ...properties
      });
    },

    trackDiagnosticTopicTapped: async (topicName, properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_TOPIC_TAPPED, {
        topic_name: topicName,
        ...properties
      });
    },

    trackDiagnosticCommunityModalOpened: async (properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_COMMUNITY_MODAL_OPENED, properties);
    },

    trackDiagnosticStartJourneyClicked: async (properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_START_JOURNEY_CLICKED, properties);
    },

    trackDiagnosticSignupCardViewed: async (properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_SIGNUP_CARD_VIEWED, properties);
    },

    // Distinct event per signup CTA tap, keyed by conversion surface
    // (source: 'results_page' | 'exit_sheet' | 'post_review').
    trackSignupCtaTapped: async (source, properties = {}) => {
      capture(EVENTS.SIGNUP_CTA_TAPPED, {
        source,
        ...properties
      });
    },

    trackExitSheetShown: async (properties = {}) => {
      capture(EVENTS.EXIT_SHEET_SHOWN, properties);
    },

    trackPostReviewCardShown: async (properties = {}) => {
      capture(EVENTS.POST_REVIEW_CARD_SHOWN, properties);
    },

    // ==========================================
    // SIGN-IN / SIGNUP MODAL EVENTS
    // ==========================================

    trackSigninModalShown: async (properties = {}) => {
      capture(EVENTS.SIGNIN_MODAL_SHOWN, properties);
    },

    trackSigninModalDismissed: async (reason, properties = {}) => {
      capture(EVENTS.SIGNIN_MODAL_DISMISSED, {
        reason,
        ...properties
      });
    },

    trackAuthMethodSelected: async (method, screen, properties = {}) => {
      capture(EVENTS.AUTH_METHOD_SELECTED, {
        method,
        screen,
        ...properties
      });
    },

    trackSigninModalSignupLinkTapped: async (properties = {}) => {
      capture(EVENTS.SIGNIN_MODAL_SIGNUP_LINK_TAPPED, properties);
    },

    trackSigninModalDiagnosticLinkTapped: async (properties = {}) => {
      capture(EVENTS.SIGNIN_MODAL_DIAGNOSTIC_LINK_TAPPED, properties);
    },

    trackSigninModalForgotPasswordTapped: async (properties = {}) => {
      capture(EVENTS.SIGNIN_MODAL_FORGOT_PASSWORD_TAPPED, properties);
    },

    trackSigninModalLearnMoreTapped: async (properties = {}) => {
      capture(EVENTS.SIGNIN_MODAL_LEARN_MORE_TAPPED, properties);
    },

    // ==========================================
    // AUTH FAILURE EVENTS (dedicated)
    // ==========================================

    trackLoginFailed: async (method, errorMessage, properties = {}) => {
      capture(EVENTS.LOGIN_FAILED, {
        login_method: method,
        error_message: errorMessage,
        ...properties
      });
    },

    trackSignupFailed: async (method, errorMessage, properties = {}) => {
      capture(EVENTS.SIGNUP_FAILED, {
        signup_method: method,
        error_message: errorMessage,
        ...properties
      });
    },

    // ==========================================
    // STUDY EVENTS
    // ==========================================

    trackStudySessionStart: async (topic, properties = {}) => {
      capture(EVENTS.STUDY_SESSION_STARTED, {
        topic,
        ...properties
      });
    },

    trackStudySessionComplete: async (topic, duration, questionsAnswered, properties = {}) => {
      capture(EVENTS.STUDY_SESSION_COMPLETED, {
        topic,
        duration_seconds: duration,
        questions_answered: questionsAnswered,
        ...properties
      });
    },

    trackQuestionBookmark: async (questionId, action, properties = {}) => {
      capture(EVENTS.QUESTION_BOOKMARKED, {
        question_id: questionId,
        action,
        ...properties
      });
    },

    trackStudyQuestionsFiltered: async (filterType, properties = {}) => {
      capture(EVENTS.STUDY_QUESTIONS_FILTERED, {
        filter_type: filterType,
        ...properties
      });
    },

    trackStudyToolSelected: async (toolName, properties = {}) => {
      capture(EVENTS.STUDY_TOOL_SELECTED, {
        tool_name: toolName,
        ...properties
      });
    },

    // ==========================================
    // TEST EVENTS
    // ==========================================

    trackTestStart: async (testType, testId, properties = {}) => {
      capture(EVENTS.TEST_STARTED, {
        test_type: testType,
        test_id: testId,
        ...properties
      });
    },

    trackTestComplete: async (testType, testId, score, duration, properties = {}) => {
      capture(EVENTS.TEST_COMPLETED, {
        test_type: testType,
        test_id: testId,
        score,
        duration_seconds: duration,
        ...properties
      });
    },

    trackTestAbandon: async (testType, testId, reason, properties = {}) => {
      capture(EVENTS.TEST_ABANDONED, {
        test_type: testType,
        test_id: testId,
        reason,
        ...properties
      });
    },

    trackCustomTestCreate: async (domainCount, questionCount, properties = {}) => {
      capture(EVENTS.CUSTOM_TEST_CREATED, {
        domain_count: domainCount,
        question_count: questionCount,
        ...properties
      });
    },

    // ==========================================
    // FLASHCARD EVENTS
    // ==========================================

    trackFlashcardBookmark: async (cardId, action, properties = {}) => {
      capture(EVENTS.FLASHCARD_BOOKMARKED, {
        card_id: cardId,
        action,
        ...properties
      });
    },

    trackFlashcardSessionStart: async (properties = {}) => {
      capture(EVENTS.FLASHCARD_SESSION_STARTED, properties);
    },

    trackFlashcardSessionEnd: async (duration, count, properties = {}) => {
      capture(EVENTS.FLASHCARD_SESSION_ENDED, {
        duration_seconds: duration,
        card_count: count,
        ...properties
      });
    },

    // ==========================================
    // SUBSCRIPTION EVENTS
    // ==========================================

    trackSubscriptionView: async (plan, properties = {}) => {
      capture(EVENTS.SUBSCRIPTION_VIEWED, {
        plan,
        ...properties
      });
    },

    // Cross-app ChoosePlan impression. Callers pass source (attribution,
    // e.g. 'article_meter_exhausted') and source_surface
    // ('pager'|'topic_list'|'related_rail') when the source has one.
    trackSubscriptionOfferViewed: async (properties = {}) => {
      capture(EVENTS.SUBSCRIPTION_OFFER_VIEWED, properties);
    },

    trackSubscriptionStart: async (plan, properties = {}) => {
      capture(EVENTS.SUBSCRIPTION_STARTED, {
        plan,
        ...properties
      });
    },

    trackSubscriptionCancel: async (plan, properties = {}) => {
      capture(EVENTS.SUBSCRIPTION_CANCELLED, {
        plan,
        ...properties
      });
    },

    trackSubscriptionCancelClicked: async (plan, properties = {}) => {
      capture(EVENTS.SUBSCRIPTION_CANCEL_CLICKED, {
        plan,
        ...properties
      });
    },

    trackCancelReasonSubmitted: async (reason, properties = {}) => {
      capture(EVENTS.CANCEL_REASON_SUBMITTED, {
        reason,
        ...properties
      });
    },

    // Intent (NOT a completed cancel) — fired when the user submits/skips the
    // pre-cancel reason screen and heads to the Stripe Portal.
    trackCancelIntent: async (reason, properties = {}) => {
      capture(EVENTS.CANCEL_INTENT, { reason, ...properties });
    },

    trackPassStoryModalShown: async (properties = {}) => {
      capture(EVENTS.PASS_STORY_MODAL_SHOWN, { ...properties });
    },

    trackPassStorySubmitted: async (properties = {}) => {
      capture(EVENTS.PASS_STORY_SUBMITTED, { ...properties });
    },

    trackPassStorySkipped: async (properties = {}) => {
      capture(EVENTS.PASS_STORY_SKIPPED, { ...properties });
    },

    trackFeedbackSubmitted: async (properties = {}) => {
      capture(EVENTS.FEEDBACK_SUBMITTED, { ...properties });
    },

    trackWhyLeftShown: async (properties = {}) => {
      capture(EVENTS.WHY_LEFT_SHOWN, { ...properties });
    },

    trackWhyLeftSubmitted: async (reason, properties = {}) => {
      capture(EVENTS.WHY_LEFT_SUBMITTED, { reason, ...properties });
    },

    trackWhyLeftDismissed: async (properties = {}) => {
      capture(EVENTS.WHY_LEFT_DISMISSED, { ...properties });
    },

    trackWinbackBannerShown: async (properties = {}) => {
      capture(EVENTS.WINBACK_BANNER_SHOWN, { ...properties });
    },

    trackWinbackBannerClicked: async (properties = {}) => {
      capture(EVENTS.WINBACK_BANNER_CLICKED, { ...properties });
    },

    trackWinbackCheckoutStarted: async (properties = {}) => {
      capture(EVENTS.WINBACK_CHECKOUT_STARTED, { ...properties });
    },

    trackPurchaseIntent: async (planName, properties = {}) => {
      capture(EVENTS.PURCHASE_INTENT_STARTED, {
        plan_name: planName,
        ...properties
      });
    },

    trackPurchaseComplete: async (planName, amount, properties = {}) => {
      capture(EVENTS.PURCHASE_COMPLETED, {
        plan_name: planName,
        amount,
        ...properties
      });
    },

    trackPromoCodeApplied: async (promoCode, properties = {}) => {
      capture(EVENTS.PROMO_CODE_APPLIED, {
        promo_code: promoCode,
        ...properties
      });
    },

    trackPromoCodeFailed: async (attemptedCode, properties = {}) => {
      capture(EVENTS.PROMO_CODE_FAILED, {
        attempted_code: attemptedCode,
        ...properties
      });
    },

    trackLicenseCodeActivated: async (properties = {}) => {
      capture(EVENTS.LICENSE_CODE_ACTIVATED, properties);
    },

    // ==========================================
    // ARTICLE EVENTS
    // ==========================================

    trackArticleView: async (articleId, properties = {}) => {
      capture(EVENTS.ARTICLE_VIEWED, {
        article_id: articleId,
        ...properties
      });
    },

    trackArticleBookmark: async (articleId, action, properties = {}) => {
      capture(EVENTS.ARTICLE_BOOKMARKED, {
        article_id: articleId,
        action,
        ...properties
      });
    },

    trackArticleProgress: async (articleId, progress, properties = {}) => {
      capture(EVENTS.ARTICLE_PROGRESS_UPDATED, {
        article_id: articleId,
        progress,
        ...properties
      });
    },

    trackArticleReaction: async (articleId, action, properties = {}) => {
      capture(EVENTS.ARTICLE_REACTION, {
        article_id: articleId,
        action,
        ...properties
      });
    },

    // Metered unlocks. Callers pass `surface`
    // ('topic_list'|'related_rail'|'pager'|'quick_set') on prompt/unlock so the
    // funnel is measurable per surface; `period_key` comes from getUnlockPeriodKey.
    trackArticleUnlock: async (articleId, properties = {}) => {
      capture(EVENTS.ARTICLE_UNLOCKED, {
        article_id: articleId,
        ...properties
      });
    },

    trackArticleUnlockPromptShown: async (articleId, properties = {}) => {
      capture(EVENTS.ARTICLE_UNLOCK_PROMPT_SHOWN, {
        article_id: articleId,
        ...properties
      });
    },

    trackArticleMeterExhaustedShown: async (properties = {}) => {
      capture(EVENTS.ARTICLE_METER_EXHAUSTED_SHOWN, properties);
    },

    // Pager lock-card impression funnel. Callers pass surface='pager' plus
    // access/reason/remaining/period_key/topic_id/index/total; impressions are
    // deduped per (article_id, reason) per pager mount by the caller.
    trackArticleInterstitialShown: async (articleId, properties = {}) => {
      capture(EVENTS.ARTICLE_INTERSTITIAL_SHOWN, {
        article_id: articleId,
        ...properties
      });
    },

    trackArticleInterstitialUpsellClicked: async (articleId, properties = {}) => {
      capture(EVENTS.ARTICLE_INTERSTITIAL_UPSELL_CLICKED, {
        article_id: articleId,
        ...properties
      });
    },

    trackArticleReadingResumed: async (articleId, properties = {}) => {
      capture(EVENTS.ARTICLE_READING_RESUMED, {
        article_id: articleId,
        ...properties
      });
    },

    trackArticlesRecommendedClicked: async (domainId, properties = {}) => {
      capture(EVENTS.ARTICLES_RECOMMENDED_CLICKED, {
        domain_id: domainId,
        ...properties
      });
    },

    // ==========================================
    // QOTD EVENTS
    // ==========================================

    trackQOTDView: async (properties = {}) => {
      capture(EVENTS.QOTD_VIEWED, properties);
    },

    trackQOTDAnswer: async (isCorrect, properties = {}) => {
      capture(EVENTS.QOTD_ANSWERED, {
        is_correct: isCorrect,
        ...properties
      });
    },

    trackQOTDSkipped: async (properties = {}) => {
      capture(EVENTS.QOTD_SKIPPED, properties);
    },

    // ==========================================
    // FEATURE USAGE EVENTS
    // ==========================================

    trackAccountDeletionAttempted: async (properties = {}) => {
      capture(EVENTS.ACCOUNT_DELETION_ATTEMPTED, properties);
    },

    trackProfileUpdate: async (field, properties = {}) => {
      capture(EVENTS.PROFILE_UPDATED, {
        field,
        ...properties
      });
    },

    trackSearch: async (query, resultsCount, properties = {}) => {
      capture(EVENTS.SEARCH_COMPLETED, {
        query,
        results_count: resultsCount,
        ...properties
      });
    },

    trackShare: async (content, platform, properties = {}) => {
      capture(EVENTS.CONTENT_SHARED, {
        content,
        platform,
        ...properties
      });
    },

    // ==========================================
    // NAVIGATION & PAGE VIEW EVENTS
    // ==========================================

    trackPageView: async (pagePath, properties = {}) => {
      capture(EVENTS.PAGE_VIEWED, {
        page_path: pagePath,
        ...properties
      });
    },

    trackNavigation: async (from, to, properties = {}) => {
      capture(EVENTS.NAVIGATION, {
        from_page: from,
        to_page: to,
        ...properties
      });
    },

    trackScreenView: async (screenName, properties = {}) => {
      capture(screenName, {
        source: 'navigation',
        ...properties
      });
    },

    // ==========================================
    // SESSION & LIFECYCLE EVENTS
    // ==========================================

    trackSessionStart: async (properties = {}) => {
      capture(EVENTS.SESSION_STARTED, properties);
    },

    trackSessionPause: async (properties = {}) => {
      capture(EVENTS.SESSION_PAUSED, properties);
    },

    trackAppOpened: async (properties = {}) => {
      capture(EVENTS.APP_OPENED, properties);
    },

    // ==========================================
    // USER ACTION EVENTS
    // ==========================================

    trackShareApp: async (properties = {}) => {
      capture(EVENTS.SHARE_APP, properties);
    },

    trackResetAppData: async (properties = {}) => {
      capture(EVENTS.RESET_APP_DATA, properties);
    },

    trackThemeChanged: async (theme, properties = {}) => {
      capture(EVENTS.APP_THEME_CHANGED, {
        theme,
        ...properties
      });
    },

    trackOfferClaimed: async (offerId, properties = {}) => {
      capture(EVENTS.OFFER_CLAIMED, {
        offer_id: offerId,
        ...properties
      });
    },

    // ==========================================
    // REVIEW & FEEDBACK EVENTS
    // ==========================================

    trackReviewApp: async (properties = {}) => {
      capture(EVENTS.REVIEW_APP, properties);
    },

    trackReviewFromHome: async (properties = {}) => {
      capture(EVENTS.REVIEW_FROM_HOME, properties);
    },

    trackReviewFeedback: async (feedbackType, properties = {}) => {
      // feedbackType: 'love_it', 'need_work', 'not_yet'
      const eventMap = {
        love_it: EVENTS.LOVE_IT_REVIEW,
        need_work: EVENTS.NEED_WORK_REVIEW,
        not_yet: EVENTS.NOT_REVIEW_YET,
      };
      capture(eventMap[feedbackType] || EVENTS.REVIEW_APP, properties);
    },

    // ==========================================
    // NOTIFICATION EVENTS
    // ==========================================

    trackNotificationPermission: async (granted, properties = {}) => {
      const event = granted
        ? EVENTS.NOTIFICATION_PERMISSION_GRANTED
        : EVENTS.NOTIFICATION_PERMISSION_DENIED;
      capture(event, properties);
    },

    // ==========================================
    // EXAM COUNTDOWN EVENTS
    // ==========================================

    trackExamCountdown: async (action, properties = {}) => {
      const eventMap = {
        set_clicked: EVENTS.EXAM_COUNTDOWN_SET_CLICKED,
        change_clicked: EVENTS.EXAM_COUNTDOWN_CHANGE_CLICKED,
        date_selected: EVENTS.EXAM_COUNTDOWN_DATE_SELECTED,
        updated: EVENTS.EXAM_COUNTDOWN_UPDATED,
      };
      capture(eventMap[action] || EVENTS.EXAM_COUNTDOWN_UPDATED, properties);
    },

    // ==========================================
    // READINESS & PROGRESS EVENTS
    // ==========================================

    trackReadinessScoreUpdated: async (score, previousScore, properties = {}) => {
      capture(EVENTS.READINESS_SCORE_UPDATED, {
        score,
        previous_score: previousScore,
        ...properties
      });
    },

    // ==========================================
    // ERROR & PERFORMANCE EVENTS
    // ==========================================

    trackAppError: async (errorType, errorMessage, properties = {}) => {
      capture(EVENTS.ERROR_OCCURRED, {
        error_type: errorType,
        error_message: errorMessage,
        ...properties
      });
    },

    trackPerformance: async (metric, value, properties = {}) => {
      capture(EVENTS.PERFORMANCE_MEASURED, {
        metric,
        value,
        ...properties
      });
    },

    trackPageLoadTime: async (loadTime, properties = {}) => {
      capture(EVENTS.PAGE_LOADED, {
        load_time_ms: loadTime,
        ...properties
      });
    },

    trackEngagement: async (action, duration, properties = {}) => {
      capture(EVENTS.ENGAGEMENT_RECORDED, {
        action,
        duration_seconds: duration,
        ...properties
      });
    },

    // ==========================================
    // MOBILE-SPECIFIC (Phase 5 union — additive; thin capture wrappers for the
    // native auth/welcome nav, mobile diagnostic flow, and aggregated menu-click
    // counters. Web never calls these.)
    // ==========================================
    trackWelcomeScreenViewed: async (properties = {}) => {
      capture(EVENTS.WELCOME_SCREEN_VIEWED, properties);
    },
    trackWelcomeSigninTapped: async (properties = {}) => {
      capture(EVENTS.WELCOME_SIGNIN_TAPPED, properties);
    },
    trackLoginScreenViewed: async (properties = {}) => {
      capture(EVENTS.LOGIN_SCREEN_VIEWED, properties);
    },
    trackSignupScreenViewed: async (properties = {}) => {
      capture(EVENTS.SIGNUP_SCREEN_VIEWED, properties);
    },
    trackSignupSigninLinkTapped: async (properties = {}) => {
      capture(EVENTS.SIGNUP_SIGNIN_LINK_TAPPED, properties);
    },
    trackDiagnosticSelectorViewed: async (properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_SELECTOR_VIEWED, properties);
    },
    trackDiagnosticPostponed: async (properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_POSTPONED, properties);
    },
    trackDiagnosticBackTapped: async (properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_BACK_TAPPED, properties);
    },
    trackDiagnosticTimerExpired: async (properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_TIMER_EXPIRED, properties);
    },
    trackDiagnosticStartPrepTapped: async (properties = {}) => {
      capture(EVENTS.DIAGNOSTIC_START_PREP_TAPPED, properties);
    },
    trackOnboardingAssessmentQuizAbandoned: async (properties = {}) => {
      capture(EVENTS.ONBOARDING_ASSESSMENT_QUIZ_ABANDONED, properties);
    },
    trackStudyQuestionsBookmarksClicked: async (properties = {}) => {
      capture(EVENTS.STUDY_QUESTIONS_BOOKMARKS_CLICKED, properties);
    },
    trackStudyQuestionsQuicksetClicked: async (properties = {}) => {
      capture(EVENTS.STUDY_QUESTIONS_QUICKSET_CLICKED, properties);
    },
    trackStudyQuestionsBytopicClicked: async (properties = {}) => {
      capture(EVENTS.STUDY_QUESTIONS_BYTOPIC_CLICKED, properties);
    },
    trackFlashcardsBookmarksClicked: async (properties = {}) => {
      capture(EVENTS.FLASHCARDS_BOOKMARKS_CLICKED, properties);
    },
    trackFlashcardsQuicksetClicked: async (properties = {}) => {
      capture(EVENTS.FLASHCARDS_QUICKSET_CLICKED, properties);
    },
    trackFlashcardsExamEssentialsClicked: async (properties = {}) => {
      capture(EVENTS.FLASHCARDS_EXAM_ESSENTIALS_CLICKED, properties);
    },
    trackFlashcardsBytopicClicked: async (properties = {}) => {
      capture(EVENTS.FLASHCARDS_BYTOPIC_CLICKED, properties);
    },
    trackArticlesBookmarksClicked: async (properties = {}) => {
      capture(EVENTS.ARTICLES_BOOKMARKS_CLICKED, properties);
    },
    trackArticlesQuicksetClicked: async (properties = {}) => {
      capture(EVENTS.ARTICLES_QUICKSET_CLICKED, properties);
    },
    trackArticlesBytopicClicked: async (properties = {}) => {
      capture(EVENTS.ARTICLES_BYTOPIC_CLICKED, properties);
    }
  };
};

export default createAnalyticsHelpers;
